import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "@tanstack/react-router";
import { useQuery } from "@powersync/react";
import { PMIcon } from "@/ds/vendor";
import { useApp } from "@/app/AppContext";
import { levelDef } from "@/lib/levels";
import { relTime } from "./format";

interface Hit { kind: "ask" | "location" | "visit" | "contact" | "file" | "page"; id: string; title: string; sub: string; emoji?: string; to: string }

// ⌘K: one box to jump anywhere. Searches the local database, so it's instant
// and works offline.
export function CommandPalette({ open, onClose }: { open: boolean; onClose: () => void }) {
  const { teamId, hierarchy } = useApp();
  const nav = useNavigate();
  const [q, setQ] = useState("");
  const [sel, setSel] = useState(0);
  const input = useRef<HTMLInputElement>(null);
  const like = `%${q.trim()}%`;
  const locs = useQuery<{ id: string; label: string; level: string; parent: string | null }>(
    `SELECT mf.id, mf.label, mf.level, (SELECT p.label FROM mobile_zone_memberships z JOIN map_features p ON p.id = z.zone_id WHERE z.member_id = mf.id LIMIT 1) AS parent
     FROM map_features mf JOIN maps m ON m.id = mf.map_id WHERE m.team_id = ? AND mf.label LIKE ? ORDER BY mf.label LIMIT 8`, [teamId, like]);
  const visits = useQuery<{ id: string; title: string; label: string | null; at: string | null }>(
    `SELECT fn.id, fn.title, mf.label, coalesce(fn.started_at, fn.created_at) AS at FROM mobile_field_notes fn LEFT JOIN map_features mf ON mf.id = fn.map_feature_id
     WHERE (fn.title LIKE ? OR mf.label LIKE ? OR fn.ai_summary LIKE ?) ORDER BY at DESC LIMIT 6`, [like, like, like]);
  const files = useQuery<{ id: string; filename: string; label: string | null; map_feature_id: string }>(
    `SELECT a.id, a.filename, mf.label, a.map_feature_id FROM mobile_field_note_attachments a LEFT JOIN map_features mf ON mf.id = a.map_feature_id WHERE a.filename LIKE ? ORDER BY a.created_at DESC LIMIT 5`, [like]);
  const contacts = useQuery<{ id: string; name: string; role: string | null }>(
    "SELECT id, name, role FROM client_contacts WHERE team_id = ? AND (name LIKE ? OR email LIKE ?) ORDER BY name LIMIT 5", [teamId, like, like]);

  const pages: Hit[] = [
    ["Home", ""], ["Field Visits", "visits"], ["Tasks", "tasks"], ["Create a task", "tasks?new=1"], ["Map", "map"], ["Team activity", "dashboards/team"], ["Sales pipeline", "dashboards/pipeline"], ["Metrics", "dashboards/metrics"],
    [hierarchy.levels[0].plural, "locations"], ["Client portfolio", "clients"], ["Paper Trail", "clients/paper-trail"], ["Ask Propel", "ask"], ["Reports", "reports"], ["Settings", "settings"],
  ].filter(([t]) => !q || t.toLowerCase().includes(q.toLowerCase())).map(([t, p]) => ({ kind: "page", id: p, title: t, sub: "Go to", to: `/t/${teamId}/${p}` }));

  // Ask Propel: any typed question (with an optional @location) becomes an answer.
  const atMatch = q.match(/@(\S+)/);
  const atLoc = atMatch ? locs.data.find((l) => l.label.toLowerCase().startsWith(atMatch[1].toLowerCase())) : undefined;
  const askQ = q.replace(/@\S+\s*/, "").trim();
  const hits: Hit[] = useMemo(() => [
    ...(q.trim().split(/\s+/).length >= 3 || q.includes("?") || atLoc ? [{ kind: "ask" as const, id: "ask", title: `Ask Propel: ${askQ || "…"}`, sub: atLoc ? `@${atLoc.label}` : "Answer from field notes", emoji: "💬", to: `/t/${teamId}/ask?${new URLSearchParams({ ...(askQ ? { q: askQ } : {}), ...(atLoc ? { at: atLoc.id } : {}) })}` }] : []),
    ...locs.data.map((l) => { const d = levelDef(l.level, hierarchy.key); return { kind: "location" as const, id: l.id, title: l.label, sub: [d.label, l.parent].filter(Boolean).join(" · "), emoji: d.emoji, to: `/t/${teamId}/locations/${l.id}` }; }),
    ...visits.data.map((v) => ({ kind: "visit" as const, id: v.id, title: v.title || "Field visit", sub: [v.label, relTime(v.at)].filter(Boolean).join(" · "), emoji: "📋", to: `/t/${teamId}/visits/${v.id}` })),
    ...contacts.data.map((c) => ({ kind: "contact" as const, id: c.id, title: c.name, sub: c.role || "Client", emoji: "👤", to: `/t/${teamId}/clients/${c.id}` })),
    ...files.data.map((f) => ({ kind: "file" as const, id: f.id, title: f.filename, sub: f.label ? `Paper Trail · ${f.label}` : "Paper Trail", emoji: "📎", to: `/t/${teamId}/clients/paper-trail?location=${f.map_feature_id}` })),
    ...pages,
  ], [locs.data, visits.data, contacts.data, files.data, pages, q, teamId, hierarchy.key, atLoc, askQ]);

  useEffect(() => { if (open) { setQ(""); setSel(0); setTimeout(() => input.current?.focus(), 10); } }, [open]);
  useEffect(() => setSel(0), [q]);
  if (!open) return null;

  const pick = (h?: Hit) => { if (!h) return; onClose(); nav({ href: h.to } as any); };
  const onKey = (e: React.KeyboardEvent) => {
    if (e.key === "Escape") onClose();
    else if (e.key === "ArrowDown") { e.preventDefault(); setSel((s) => Math.min(s + 1, hits.length - 1)); }
    else if (e.key === "ArrowUp") { e.preventDefault(); setSel((s) => Math.max(s - 1, 0)); }
    else if (e.key === "Enter") pick(hits[sel]);
  };

  let group = "";
  return (
    <div className="palette-scrim" onClick={onClose}>
      <div className="palette" role="dialog" aria-label="Search" onClick={(e) => e.stopPropagation()} onKeyDown={onKey}>
        <div className="palette-input">
          <PMIcon name="search-outline" size={20} />
          <input ref={input} value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search, jump, or ask a question (try @ to tag a location)" aria-label="Search" />
          <kbd>esc</kbd>
        </div>
        <div className="palette-list" role="listbox">
          {hits.length === 0 && <div className="palette-empty">No matches for “{q}”</div>}
          {hits.map((h, i) => {
            const label = { ask: "Ask Propel", location: hierarchy.levels[0].plural, visit: "Field Visits", contact: "Clients", file: "Paper Trail", page: "Go to" }[h.kind];
            const head = label !== group ? (group = label) : null;
            return (
              <div key={h.kind + h.id}>
                {head && <div className="palette-group">{head}</div>}
                <button role="option" aria-selected={i === sel} className={`palette-row ${i === sel ? "is-sel" : ""}`} onMouseEnter={() => setSel(i)} onClick={() => pick(h)}>
                  <span className="palette-emoji">{h.emoji ?? "›"}</span>
                  <span className="palette-title">{h.title}</span>
                  <span className="palette-sub">{h.sub}</span>
                </button>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
